import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  SimpleGrid,
  Button,
  Flex,
  Heading,
  Text,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  IconButton,
  Tooltip,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  Checkbox,
  Textarea
} from "@chakra-ui/react";
import { 
  FiPlus, 
  FiEye, 
  FiEdit, 
  FiTrash2, 
  FiDollarSign,
  FiUsers,
  FiCalendar,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertTriangle,
  FiFileText
} from "react-icons/fi";
import { useUserContext } from "../../../context/UserContext";
import { useAccessControl } from "../../../hooks/useAccessControl";
import { 
  useGetPayrollBatches, 
  useCreatePayrollBatch,
  useUpdatePayrollBatch,
  useDeletePayrollBatch,
  useGetEmployeesForPayroll 
} from "../../../services/payrollService";
import { useGetPlanInfo } from "../../../services/planService";
import { showSuccessAlert, showErrorAlert } from "../../../utils/alert";
import { formatCurrency, getCurrencySymbol, getSupportedCountries, getCurrencyForCountry } from "../../../utils/currency";
import MonthSelector from "../../../components/MonthSelector";
import YearSelector from "../../../components/yearSelector";

const BatchManagement = () => {
  const { user } = useUserContext();
  const { 
    canCreateBatch,
    filterBatchesByRole,
    customerRoles
  } = useAccessControl();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Get plan info for country-based currency
  const { planInfo } = useGetPlanInfo();
  
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const cardBg = useColorModeValue("white", "gray.700");

  // State declarations
  const [bankDetailsFilter, setBankDetailsFilter] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);

  // Build filters for employees API
  const employeeFilters = useMemo(() => {
    const filters = {};
    if (selectedMonth?.value) {
      filters.month = selectedMonth.value;
    }
    if (selectedYear?.value) {
      filters.year = selectedYear.value;
    }
    return filters;
  }, [selectedMonth, selectedYear]);

  // Real API data
  const { 
    data: batchesData, 
    isLoading: batchesLoading, 
    error: batchesError 
  } = useGetPayrollBatches({ bank_details_filter: bankDetailsFilter });
  
  const { 
    data: employeesData, 
    isLoading: employeesLoading, 
    error: employeesError 
  } = useGetEmployeesForPayroll(employeeFilters);
  
  const createBatchMutation = useCreatePayrollBatch();
  const updateBatchMutation = useUpdatePayrollBatch();
  const deleteBatchMutation = useDeletePayrollBatch();
  
  // Process API data
  const allBatches = batchesData?.batches || [];
  // Filter batches based on user role
  const batches = filterBatchesByRole(allBatches, user, customerRoles);
  const allEmployees = employeesData?.employees || [];
  const periodInfo = employeesData?.period_info || null;
  
  // Filter employees based on bank details filter
  const filteredEmployees = useMemo(() => {
    if (bankDetailsFilter === "with_bank") {
      return allEmployees.filter(emp => emp.bank_details_available === true);
    } else if (bankDetailsFilter === "without_bank") {
      return allEmployees.filter(emp => emp.bank_details_available === false);
    }
    return allEmployees;
  }, [allEmployees, bankDetailsFilter]);
  
  const employees = filteredEmployees;

  const [formData, setFormData] = useState({
    batch_name: "",
    currency: "USD",
    employees: [],
    payment_type: "regular_pay"
  });

  // Set default currency based on plan country
  useEffect(() => {
    if (planInfo?.country) {
      const currencyInfo = getCurrencyForCountry(planInfo.country);
      setFormData(prev => ({
        ...prev,
        currency: currencyInfo.code
      }));
    }
  }, [planInfo?.country]);

  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [paymentType, setPaymentType] = useState("regular_pay");
  const [employeePaymentTypes, setEmployeePaymentTypes] = useState({}); // Individual payment types per employee

  // Get currencies from supported countries
  const supportedCountries = getSupportedCountries();
  const currencies = supportedCountries.map(country => {
    const currencyInfo = getCurrencySymbol(country.name === 'Other' ? 'United States' : country.name);
    return {
      code: currencyInfo,
      name: country.currency,
      symbol: currencyInfo
    };
  });

  const payTypes = [
    { value: "regular_pay", label: "Regular Pay" },
    { value: "total_pay", label: "Total Pay" },
    { value: "net_salary", label: "Net Salary" }
  ];

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'green';
      case 'pending_hr_approval': 
      case 'pending_hr_approval': return 'orange';
      case 'pending_accountant_approval': 
      case 'pending_accountant_approval': return 'blue';
      case 'processing': return 'purple';
      case 'failed': return 'red';
      case 'draft': return 'gray';
      default: return 'gray';
    }
  };

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'Completed';
      case 'pending_hr_approval': 
      case 'pending_hr_approval': return 'Pending HR Approval';
      case 'pending_accountant_approval': 
      case 'pending_accountant_approval': return 'Pending Accountant Approval';
      case 'processing': return 'Processing';
      case 'failed': return 'Failed';
      case 'draft': return 'Draft';
      default: return status || 'Unknown';
    }
  };

  const handleCreateBatch = () => {
    setSelectedBatch(null);
    setIsEditing(false);
    setFormData({
      batch_name: "",
      currency: "USD",
      employees: []
    });
    setSelectedEmployees([]);
    onOpen();
  };

  const handleEditBatch = (batch) => {
    setSelectedBatch(batch);
    setIsEditing(true);
    setFormData({
      batch_name: batch.batch_name,
      currency: batch.currency,
      employees: batch.employees
    });
    setSelectedEmployees(batch.employees.map(emp => emp.employee_id));
    onOpen();
  };

  const handleViewBatch = (batch) => {
    setSelectedBatch(batch);
    onViewOpen();
  };

  const handleSaveBatch = () => {
    if (!formData.batch_name.trim()) {
      showErrorAlert("Batch name is required");
      return;
    }
    
    if (selectedEmployees.length === 0) {
      showErrorAlert("Please select at least one employee");
      return;
    }

    // Build the batch payload according to the new API structure
    const selectedEmployeeData = selectedEmployees.map(empId => {
      const employee = allEmployees.find(emp => emp.employee_id === empId);
      const chosenPaymentType = employeePaymentTypes[empId] || paymentType; // Individual or global payment type
      const chosenPayment = employee.payment_options?.[chosenPaymentType];
      
      return {
        employee_id: employee.employee_id,
        basic_salary: employee.payment_options?.regular_pay?.base_salary || employee.base_salary || 0,
        allowances: 0, // Default to 0 as per your specification
        deductions: chosenPayment?.total_deductions || 0,
        net_salary: chosenPayment?.net_amount || 0,
        currency: chosenPayment?.currency || formData.currency,
        tax_percentage: chosenPayment?.tax_percentage || 0,
        tax_amount: chosenPayment?.tax_amount || 0,
        pay_type: chosenPaymentType,
        regular_pay: employee.payment_options?.regular_pay?.base_salary || 0,
        total_pay: employee.payment_options?.total_pay?.total_pay || 0,
        hourly_salary: employee.payment_options?.total_pay?.hourly_salary || 0,
        total_hours_worked: employee.attendance_summary?.total_hours_worked?.toString() || "0",
        overtime_hours: employee.attendance_summary?.overtime_hours?.toString() || "0",
        missed_hours: employee.attendance_summary?.missed_hours?.toString() || "0",
        overtime_pay: employee.payment_options?.total_pay?.overtime_pay || 0,
        missed_hours_deduction: employee.payment_options?.total_pay?.missed_hours_deduction || 0,
        leave_allowance: employee.payment_options?.total_pay?.leave_allowance || 0,
        bonus: employee.payment_options?.total_pay?.bonus || employee.payment_options?.total_pay?.thirteenth_month_bonus || 0
      };
    });

    // Calculate total amount from selected employees
    const totalAmount = selectedEmployeeData.reduce((sum, emp) => sum + emp.net_salary, 0);

    const batchPayload = {
      batch_name: formData.batch_name,
      total_amount: totalAmount,
      currency: formData.currency,
      employees: selectedEmployeeData
    };
    
    if (isEditing && selectedBatch) {
      updateBatchMutation.mutate(
        { batchId: selectedBatch.batch_id, batchData: batchPayload },
        {
          onSuccess: () => {
            showSuccessAlert("Batch updated successfully!");
            onClose();
            const defaultCurrency = planInfo?.country 
              ? getCurrencyForCountry(planInfo.country).code 
              : "USD";
            setFormData({
              batch_name: "",
              currency: defaultCurrency,
              employees: [],
              payment_type: "regular_pay"
            });
            setSelectedEmployees([]);
            setPaymentType("regular_pay");
            setEmployeePaymentTypes({});
          },
          onError: (error) => {
            showErrorAlert("Failed to update batch: " + (error.message || "Unknown error"));
          }
        }
      );
    } else {
      createBatchMutation.mutate(batchPayload, {
        onSuccess: (data) => {
          showSuccessAlert("Batch created successfully!");
    onClose();
          const defaultCurrency = planInfo?.country 
            ? getCurrencyForCountry(planInfo.country).code 
            : "USD";
          setFormData({
            batch_name: "",
            currency: defaultCurrency,
            employees: [],
            payment_type: "regular_pay"
          });
          setSelectedEmployees([]);
          setPaymentType("regular_pay");
          setEmployeePaymentTypes({});
        },
        onError: (error) => {
          showErrorAlert("Failed to create batch: " + (error.message || "Unknown error"));
        }
      });
    }
  };

  const handleEmployeeSelect = (employeeId, isSelected) => {
    if (isSelected) {
      setSelectedEmployees(prev => [...prev, employeeId]);
    } else {
      setSelectedEmployees(prev => prev.filter(id => id !== employeeId));
      // Remove individual payment type when employee is deselected
      setEmployeePaymentTypes(prev => {
        const newTypes = { ...prev };
        delete newTypes[employeeId];
        return newTypes;
      });
    }
  };

  const handleEmployeePaymentTypeChange = (employeeId, paymentType) => {
    setEmployeePaymentTypes(prev => ({
      ...prev,
      [employeeId]: paymentType
    }));
  };

  // Use the new currency utility function
  const getCurrencySymbolForDisplay = (currency) => {
    return getCurrencySymbol(planInfo?.country, currency);
  };

  // Show loading state
  if (batchesLoading || employeesLoading) {
    return (
      <VStack spacing={6} align="stretch">
        <Flex justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <Text color="gray.600" fontSize="sm">
              Loading batch data...
            </Text>
          </VStack>
        </Flex>
        <Box textAlign="center" py={8}>
          <Text color="gray.500">Loading payroll batches...</Text>
        </Box>
      </VStack>
    );
  }

  // Show error state
  if (batchesError || employeesError) {
    return (
      <VStack spacing={6} align="stretch">
        <Flex justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <Text color="gray.600" fontSize="sm">
              Error loading batch data
            </Text>
          </VStack>
        </Flex>
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error loading batch data!</AlertTitle>
          <AlertDescription>
            {batchesError?.message || employeesError?.message || 'Failed to load batch data'}
          </AlertDescription>
        </Alert>
      </VStack>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Flex justify="space-between" align="center">
        <VStack align="start" spacing={1}>
          <Text color="gray.600" fontSize="sm">
            {/* Create and manage payroll batches for processing */}
          </Text>
        </VStack>
        {canCreateBatch() && (
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            onClick={handleCreateBatch}
            size="md"
            borderRadius="lg"
          >
            Create Batch
          </Button>
        )}
      </Flex>

      {/* Filter Section */}
      <Box p={4} bg={cardBg} borderRadius="lg" border="1px solid" borderColor={borderColor}>
        <VStack spacing={4} align="stretch">
          <Heading size="sm" color="gray.800">
            Filter Batches
          </Heading>
          <HStack spacing={4} align="center">
            <Text fontSize="sm" color="gray.600" minW="120px">
              Bank Details:
            </Text>
            <Select
              value={bankDetailsFilter}
              onChange={(e) => setBankDetailsFilter(e.target.value)}
              size="sm"
              maxW="200px"
            >
              <option value="all">All Employees</option>
              <option value="with_bank">With Bank Details</option>
              <option value="without_bank">Without Bank Details</option>
            </Select>
            {batchesData?.filter_info && (
              <Text fontSize="sm" color="gray.500">
                {batchesData.filter_info.description}
              </Text>
            )}
          </HStack>
        </VStack>
      </Box>

      {/* Filter Summary */}
      {batchesData?.filter_info && (
        <Alert status="info" borderRadius="lg">
          <AlertIcon />
          <Box>
            <AlertTitle>Filter Applied</AlertTitle>
            <AlertDescription>
              {batchesData.filter_info.description} - Showing {batchesData.total_count} batch(es)
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* Batch Statistics */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
        <Box bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <VStack spacing={2} align="stretch">
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.600">Total Batches</Text>
              <FiFileText color="blue.500" />
            </HStack>
            <Text fontSize="2xl" fontWeight="700" color="gray.800">
              {batches.length}
            </Text>
          </VStack>
        </Box>

        <Box bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <VStack spacing={2} align="stretch">
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.600">Pending HR</Text>
              <FiClock color="orange.500" />
            </HStack>
            <Text fontSize="2xl" fontWeight="700" color="gray.800">
              {batches.filter(b => b.status?.toLowerCase() === 'pending_hr_approval').length}
            </Text>
          </VStack>
        </Box>

        <Box bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <VStack spacing={2} align="stretch">
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.600">Pending Accountant</Text>
              <FiCheckCircle color="blue.500" />
            </HStack>
            <Text fontSize="2xl" fontWeight="700" color="gray.800">
              {batches.filter(b => b.status?.toLowerCase() === 'pending_accountant_approval').length}
            </Text>
          </VStack>
        </Box>

        <Box bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <VStack spacing={2} align="stretch">
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.600">Completed</Text>
              <FiCheckCircle color="green.500" />
            </HStack>
            <Text fontSize="2xl" fontWeight="700" color="gray.800">
              {batches.filter(b => b.status?.toLowerCase() === 'completed').length}
            </Text>
          </VStack>
        </Box>
      </SimpleGrid>

      {/* Batches Table */}
      <Box
        bg={cardBg}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
        overflow="hidden"
      >
        <TableContainer>
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Batch Name</Th>
                <Th>Status</Th>
                <Th>Total Amount</Th>
                <Th>Employees</Th>
                <Th>Created By</Th>
                <Th>Created Date</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {batches.map((batch) => (
                <Tr key={batch.batch_id}>
                  <Td>
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="600" color="gray.800">
                        {batch.batch_name}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {batch.batch_id}
                      </Text>
                    </VStack>
                  </Td>
                  <Td>
                    <Badge colorScheme={getStatusColor(batch.status)}>
                      {getStatusText(batch.status)}
                    </Badge>
                  </Td>
                  <Td>
                    <Text fontWeight="600" color="gray.800">
                      {formatCurrency(batch.total_amount || 0, planInfo?.country)}
                    </Text>
                  </Td>
                  <Td>
                    <VStack align="start" spacing={1}>
                    <HStack spacing={1}>
                      <FiUsers color="gray.500" />
                        <Text>{batch.payment_summary?.filtered_employee_count || batch.employee_count}</Text>
                    </HStack>
                      {batch.payment_summary?.filtered_employee_count && batch.payment_summary.filtered_employee_count !== batch.employee_count && (
                        <Text fontSize="xs" color="gray.500">
                          of {batch.employee_count} total
                        </Text>
                      )}
                    </VStack>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color="gray.800" fontWeight="600">
                      {typeof batch.created_by === 'object' ? batch.created_by?.name || 'Unknown' : batch.created_by || 'Unknown'}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color="gray.600">
                      {new Date(batch.created_at).toLocaleDateString()}
                    </Text>
                  </Td>
                  <Td>
                    <Tooltip label="View Details">
                      <IconButton
                        icon={<FiEye />}
                        size="sm"
                        variant="ghost"
                        colorScheme="blue"
                        aria-label="View details"
                        onClick={() => handleViewBatch(batch)}
                      />
                    </Tooltip>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      {/* Create/Edit Batch Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isEditing ? "Edit Payroll Batch" : "Create Payroll Batch"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={6} align="stretch">
              {/* Basic Information */}
              <Box>
                <Heading size="sm" color="gray.800" mb={4}>
                  Batch Information
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Batch Name</FormLabel>
                    <Input
                      value={formData.batch_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, batch_name: e.target.value }))}
                      placeholder="e.g., September 2024 Payroll"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Currency</FormLabel>
                    <Select
                      value={formData.currency}
                      onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                    >
                      {currencies.map((currency) => (
                        <option key={currency.code} value={currency.code}>
                          {currency.name} ({currency.symbol})
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </SimpleGrid>
              </Box>

              <Divider />

              {/* Period Selection */}
              <Box>
                <VStack spacing={4} align="stretch">
                  <Heading size="sm" color="gray.800">
                    Payroll Period
                  </Heading>
                  <HStack spacing={4} align="end">
                    <Box flex={1}>
                      <FormLabel fontSize="sm" color="gray.600" mb={2}>
                        Month
                      </FormLabel>
                      <MonthSelector
                        onChange={setSelectedMonth}
                        selectedYear={selectedYear}
                      />
                    </Box>
                    <Box flex={1}>
                      <FormLabel fontSize="sm" color="gray.600" mb={2}>
                        Year
                      </FormLabel>
                      <YearSelector
                        onChange={setSelectedYear}
                        selectedYear={selectedYear}
                      />
                    </Box>
                    <Button
                      size="md"
                      variant="outline"
                      onClick={() => {
                        setSelectedMonth(null);
                        setSelectedYear(null);
                      }}
                    >
                      Current Period
                    </Button>
                  </HStack>
                  
                  {/* Period Info Display */}
                  {periodInfo && (
                    <Alert status="info" borderRadius="md">
                      <AlertIcon />
                      <Box>
                        <Text fontSize="sm" fontWeight="600">
                          Showing payroll for: {new Date(periodInfo.year, periodInfo.month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </Text>
                        <Text fontSize="xs" color="gray.600" mt={1}>
                          Period: {new Date(periodInfo.start_date).toLocaleDateString()} - {new Date(periodInfo.end_date).toLocaleDateString()}
                          {periodInfo.is_current_period && " (Current Period)"}
                        </Text>
                      </Box>
                    </Alert>
                  )}
                </VStack>
              </Box>

              <Divider />

              {/* Employee Selection */}
              <Box>
                <VStack spacing={4} align="stretch">
                  <Heading size="sm" color="gray.800">
                    Select Employees for Payroll Batch
                </Heading>
                  
                  {/* Filter Section */}
                  <Box p={3} bg="gray.50" borderRadius="md">
                    <HStack spacing={4} align="center">
                      <Text fontSize="sm" fontWeight="600" color="gray.700" minW="120px">
                        Filter by Bank Details:
                      </Text>
                      <Select
                        value={bankDetailsFilter}
                        onChange={(e) => setBankDetailsFilter(e.target.value)}
                        size="sm"
                        maxW="200px"
                      >
                        <option value="all">All Employees ({allEmployees.length})</option>
                        <option value="with_bank">With Bank Details ({allEmployees.filter(emp => emp.bank_details_available).length})</option>
                        <option value="without_bank">Without Bank Details ({allEmployees.filter(emp => !emp.bank_details_available).length})</option>
                      </Select>
                      <Text fontSize="xs" color="gray.500">
                        Showing {employees.length} of {allEmployees.length} employees
                      </Text>
                    </HStack>
                  </Box>

                  {/* Payment Type Selection */}
                  <Box p={3} bg="blue.50" borderRadius="md">
                    <VStack spacing={3} align="stretch">
                      <Text fontSize="sm" fontWeight="600" color="blue.800">
                        💰 Payment Calculation Method
                      </Text>
                      <HStack spacing={6}>
                        <HStack>
                          <input
                            type="radio"
                            id="regular_pay"
                            name="paymentType"
                            value="regular_pay"
                            checked={paymentType === "regular_pay"}
                            onChange={(e) => setPaymentType(e.target.value)}
                          />
                          <label htmlFor="regular_pay">
                            <VStack align="start" spacing={0}>
                              <Text fontSize="sm" fontWeight="600">Regular Pay</Text>
                              <Text fontSize="xs" color="gray.600">Base salary + tax + deductions</Text>
                            </VStack>
                          </label>
                        </HStack>
                        <HStack>
                          <input
                            type="radio"
                            id="total_pay"
                            name="paymentType"
                            value="total_pay"
                            checked={paymentType === "total_pay"}
                            onChange={(e) => setPaymentType(e.target.value)}
                          />
                          <label htmlFor="total_pay">
                            <VStack align="start" spacing={0}>
                              <Text fontSize="sm" fontWeight="600">Total Pay</Text>
                              <Text fontSize="xs" color="gray.600">Total hours worked pay + tax + all deductions</Text>
                            </VStack>
                          </label>
                        </HStack>
                      </HStack>
                    </VStack>
                  </Box>

                  {/* Employee Table */}
                  <Box border="1px solid" borderColor={borderColor} borderRadius="md" overflow="hidden">
                    <TableContainer maxH="400px" overflowY="auto">
                      <Table variant="simple" size="sm">
                        <Thead bg="gray.50" position="sticky" top={0}>
                          <Tr>
                            <Th>
                              <Checkbox
                                isChecked={selectedEmployees.length === employees.length && employees.length > 0}
                                isIndeterminate={selectedEmployees.length > 0 && selectedEmployees.length < employees.length}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedEmployees(employees.map(emp => emp.employee_id));
                                  } else {
                                    setSelectedEmployees([]);
                                    setEmployeePaymentTypes({});
                                  }
                                }}
                              />
                            </Th>
                            <Th>Employee</Th>
                            <Th>Department</Th>
                            <Th>Base Salary</Th>
                            <Th>Tax Amount</Th>
                            <Th>Bank Details</Th>
                            <Th>Payment Type</Th>
                            <Th>Net Amount</Th>
                            <Th>Transfer Charge</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {employees.length > 0 ? (
                            employees.map((employee) => {
                              const individualPaymentType = employeePaymentTypes[employee.employee_id] || paymentType;
                              const paymentOption = employee.payment_options?.[individualPaymentType];
                              const netAmount = paymentOption?.net_amount || 0;
                              const transferCharge = paymentOption?.flutterwave_fee || 0;
                              const taxAmount = paymentOption?.tax_amount || 0;
                              const currency = paymentOption?.currency || 'NGN';
                              
                              return (
                                <Tr 
                                  key={employee.employee_id}
                                  bg={selectedEmployees.includes(employee.employee_id) ? "blue.50" : "white"}
                                  _hover={{ bg: selectedEmployees.includes(employee.employee_id) ? "blue.100" : "gray.50" }}
                                >
                                  <Td>
                                    <Checkbox
                                      isChecked={selectedEmployees.includes(employee.employee_id)}
                                      onChange={(e) => handleEmployeeSelect(employee.employee_id, e.target.checked)}
                                    />
                                  </Td>
                                  <Td>
                        <VStack align="start" spacing={1}>
                                      <Text fontWeight="600" fontSize="sm" color="gray.800">
                                        {employee.full_name}
                                      </Text>
                                      <Text fontSize="xs" color="gray.500">
                                        ID: {employee.employee_id}
                          </Text>
                                    </VStack>
                                  </Td>
                                  <Td>
                          <Text fontSize="sm" color="gray.600">
                                      {employee.designation || 'N/A'}
                                    </Text>
                                  </Td>
                                  <Td>
                                    <Text fontSize="sm" fontWeight="600" color="gray.800">
                                      {formatCurrency(parseFloat(employee.salary || 0), planInfo?.country)}
                                    </Text>
                                  </Td>
                                  <Td>
                                    <Text fontSize="sm" fontWeight="500" color="red.600">
                                      {formatCurrency(taxAmount, planInfo?.country)}
                                    </Text>
                                  </Td>
                                  <Td>
                                    <HStack spacing={1}>
                                      {employee.bank_details_available ? (
                                        <>
                                          <FiCheckCircle color="green" size={14} />
                                          <Text fontSize="xs" color="green.600">Available</Text>
                                        </>
                                      ) : (
                                        <>
                                          <FiXCircle color="red" size={14} />
                                          <Text fontSize="xs" color="red.600">Missing</Text>
                                        </>
                                      )}
                                    </HStack>
                                  </Td>
                                  <Td>
                                    <Select
                                      size="xs"
                                      value={individualPaymentType}
                                      onChange={(e) => handleEmployeePaymentTypeChange(employee.employee_id, e.target.value)}
                                      maxW="120px"
                                    >
                                      <option value="regular_pay">Regular Pay</option>
                                      <option value="total_pay">Total Pay</option>
                                    </Select>
                                  </Td>
                                  <Td>
                                    <VStack align="start" spacing={0}>
                                      <Text fontSize="sm" fontWeight="600" color="green.600">
                                        {formatCurrency(netAmount, planInfo?.country)}
                                      </Text>
                                      <Text fontSize="xs" color="gray.500">
                                        {individualPaymentType === 'regular_pay' ? 'Regular' : 'Total'} Pay
                                      </Text>
                                    </VStack>
                                  </Td>
                                  <Td>
                                    <Text fontSize="sm" fontWeight="500" color="purple.600">
                                      {formatCurrency(transferCharge, planInfo?.country)}
                                    </Text>
                                  </Td>
                                </Tr>
                              );
                            })
                          ) : (
                            <Tr>
                              <Td colSpan={9} textAlign="center" py={8}>
                                <VStack spacing={2}>
                                  <FiUsers size={24} color="gray" />
                                  <Text color="gray.500">No employees available for payroll</Text>
                                  <Text fontSize="sm" color="gray.400">
                                    {bankDetailsFilter === "with_bank" 
                                      ? "No employees with bank details found"
                                      : bankDetailsFilter === "without_bank"
                                      ? "No employees without bank details found"
                                      : "No employees found in the system"
                                    }
                                  </Text>
                                </VStack>
                              </Td>
                            </Tr>
                          )}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {/* Selection Summary */}
                  {selectedEmployees.length > 0 && (
                    <Box p={3} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                      <HStack justify="space-between" align="center">
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm" fontWeight="600" color="green.800">
                            ✅ {selectedEmployees.length} employee(s) selected
                          </Text>
                          <VStack align="start" spacing={1}>
                            <Text fontSize="xs" color="green.600">
                              Net Salaries: {formatCurrency(selectedEmployees.reduce((total, empId) => {
                                const employee = employees.find(emp => emp.employee_id === empId);
                                const individualPaymentType = employeePaymentTypes[empId] || paymentType;
                                const paymentOption = employee?.payment_options?.[individualPaymentType];
                                return total + (paymentOption?.net_amount || 0);
                              }, 0), planInfo?.country)}
                            </Text>
                            <Text fontSize="xs" color="purple.600">
                              Transfer Charges: {formatCurrency(selectedEmployees.reduce((total, empId) => {
                                const employee = employees.find(emp => emp.employee_id === empId);
                                const individualPaymentType = employeePaymentTypes[empId] || paymentType;
                                const paymentOption = employee?.payment_options?.[individualPaymentType];
                                return total + (paymentOption?.flutterwave_fee || 0);
                              }, 0), planInfo?.country)}
                            </Text>
                            <Text fontSize="xs" color="blue.600" fontWeight="600">
                              Total Estimated: {formatCurrency(selectedEmployees.reduce((total, empId) => {
                                const employee = employees.find(emp => emp.employee_id === empId);
                                const individualPaymentType = employeePaymentTypes[empId] || paymentType;
                                const paymentOption = employee?.payment_options?.[individualPaymentType];
                                return total + (paymentOption?.net_amount || 0) + (paymentOption?.flutterwave_fee || 0);
                              }, 0), planInfo?.country)}
                            </Text>
                          </VStack>
                        </VStack>
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="green"
                          onClick={() => {
                            setSelectedEmployees([]);
                            setEmployeePaymentTypes({});
                          }}
                        >
                          Clear Selection
                        </Button>
                      </HStack>
                    </Box>
                  )}
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleSaveBatch}
              isLoading={createBatchMutation.isPending || updateBatchMutation.isPending}
              loadingText={isEditing ? "Updating..." : "Creating..."}
            >
              {isEditing ? "Update" : "Create"} Batch
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* View Batch Details Modal */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Batch Details - {selectedBatch?.batch_name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedBatch && (
              <VStack spacing={6} align="stretch">
                {/* Batch Information */}
                <Box>
                  <Heading size="sm" color="gray.800" mb={4}>
                    Batch Information
                  </Heading>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Box p={3} bg="gray.50" borderRadius="md">
                      <Text fontSize="sm" color="gray.600">Batch ID</Text>
                      <Text fontWeight="600">{selectedBatch.batch_id}</Text>
                    </Box>
                    <Box p={3} bg="gray.50" borderRadius="md">
                      <Text fontSize="sm" color="gray.600">Status</Text>
                      <Badge colorScheme={getStatusColor(selectedBatch.status)}>
                        {getStatusText(selectedBatch.status)}
                      </Badge>
                    </Box>
                    <Box p={3} bg="gray.50" borderRadius="md">
                      <Text fontSize="sm" color="gray.600">Created By</Text>
                      <Text fontWeight="600">
                        {typeof selectedBatch.created_by === 'object' ? selectedBatch.created_by?.name || 'Unknown' : selectedBatch.created_by || 'Unknown'}
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                <Divider />

                {/* Payment Summary */}
                {(selectedBatch.payment_summary || selectedBatch.batch_items || selectedBatch.items) && (
                  <Box>
                    <Heading size="sm" color="gray.800" mb={4}>
                      Payment Summary
                    </Heading>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                      <Box p={4} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                        <Text fontSize="sm" color="green.700" fontWeight="500" mb={1}>
                          Total Net Salaries
                        </Text>
                        <Text fontSize="xl" fontWeight="700" color="green.900">
                          {formatCurrency(
                            selectedBatch.payment_summary?.total_net_salaries || 
                            selectedBatch.total_amount || 
                            (selectedBatch.batch_items || selectedBatch.items || []).reduce((sum, item) => sum + (item.net_salary || 0), 0) || 
                            0, 
                            planInfo?.country
                          )}
                        </Text>
                        <Text fontSize="xs" color="green.600" mt={1}>
                          Employees Receive
                        </Text>
                      </Box>
                      <Box p={4} bg="purple.50" borderRadius="md" border="1px solid" borderColor="purple.200">
                        <Text fontSize="sm" color="purple.700" fontWeight="500" mb={1}>
                          Transfer Charges
                        </Text>
                        <Text fontSize="xl" fontWeight="700" color="purple.900">
                          {formatCurrency(selectedBatch.total_transfer_charges || 0, planInfo?.country)}
                        </Text>
                        <Text fontSize="xs" color="purple.600" mt={1}>
                          Payment Processing Fees
                        </Text>
                      </Box>
                      <Box p={4} bg="teal.50" borderRadius="md" border="1px solid" borderColor="teal.200">
                        <Text fontSize="sm" color="teal.700" fontWeight="500" mb={1}>
                          Processing Fee (0.5%)
                        </Text>
                        <Text fontSize="xl" fontWeight="700" color="teal.900">
                          {formatCurrency(selectedBatch.processing_fees_paid || 0, planInfo?.country)}
                        </Text>
                        {(() => {
                          const items = selectedBatch.batch_items || selectedBatch.items || [];
                          const employeeCount = items.length || selectedBatch.payment_summary?.total_employees || 0;
                          const perEmployeeFee = employeeCount > 0 
                            ? (selectedBatch.processing_fees_paid || 0) / employeeCount 
                            : 0;
                          return employeeCount > 0 && perEmployeeFee > 0 ? (
                            <Text fontSize="xs" color="teal.600" mt={1}>
                              {formatCurrency(perEmployeeFee, planInfo?.country)} × {employeeCount} employees
                            </Text>
                          ) : (
                            <Text fontSize="xs" color="teal.600" mt={1}>
                              Service Fee
                            </Text>
                          );
                        })()}
                      </Box>
                      <Box p={4} bg="orange.50" borderRadius="md" border="1px solid" borderColor="orange.200">
                        <Text fontSize="sm" color="orange.700" fontWeight="500" mb={1}>
                          Total Settlement
                        </Text>
                        <Text fontSize="xl" fontWeight="700" color="orange.900">
                          {formatCurrency(
                            selectedBatch.payment_summary?.total_settlement_amount || 
                            (selectedBatch.batch_items || selectedBatch.items || []).reduce((sum, item) => sum + ((item.tax_amount || 0) + (item.deductions || 0)), 0) || 
                            0, 
                            planInfo?.country
                          )}
                        </Text>
                        <Text fontSize="xs" color="orange.600" mt={1}>
                          Company Pays (Tax + Deductions)
                        </Text>
                      </Box>
                      <Box p={4} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
                        <Text fontSize="sm" color="blue.700" fontWeight="500" mb={1}>
                          Total Company Cost
                        </Text>
                        <Text fontSize="xl" fontWeight="700" color="blue.900">
                          {formatCurrency(
                            (selectedBatch.payment_summary?.total_net_salaries || selectedBatch.total_amount || (selectedBatch.batch_items || selectedBatch.items || []).reduce((sum, item) => sum + (item.net_salary || 0), 0) || 0) + 
                            (selectedBatch.total_transfer_charges || (selectedBatch.batch_items || selectedBatch.items || []).reduce((sum, item) => sum + (item.flutterwave_fee || 0), 0) || 0) +
                            (selectedBatch.processing_fees_paid || 0) +
                            (selectedBatch.payment_summary?.total_settlement_amount || (selectedBatch.batch_items || selectedBatch.items || []).reduce((sum, item) => sum + ((item.tax_amount || 0) + (item.deductions || 0)), 0) || 0),
                            planInfo?.country
                          )}
                        </Text>
                        <Text fontSize="xs" color="blue.600" mt={1}>
                          All Costs Combined
                        </Text>
                      </Box>
                      <Box p={4} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                        <Text fontSize="sm" color="gray.700" fontWeight="500" mb={1}>
                          Payment Status
                        </Text>
                        <Badge 
                          colorScheme={selectedBatch.payment_summary?.ready_for_payment ? 'green' : 'yellow'} 
                          fontSize="md" 
                          p={2}
                        >
                          {selectedBatch.payment_summary?.ready_for_payment ? 'Ready' : 'Not Ready'}
                        </Badge>
                        <Text fontSize="xs" color="gray.600" mt={1}>
                          {selectedBatch.payment_summary?.total_deductions || (selectedBatch.batch_items || selectedBatch.items || []).reduce((sum, item) => sum + (item.deductions || 0), 0) || 0} Deductions
                        </Text>
                      </Box>
                    </SimpleGrid>
                  </Box>
                )}

                <Divider />

                {/* Next Action */}
                <Box>
                  <Heading size="sm" color="gray.800" mb={4}>
                    Next Action Required
                  </Heading>
                  <Alert status={selectedBatch.status?.toLowerCase() === 'pending_hr_approval' ? 'warning' : 'info'}>
                    <AlertIcon />
                    <Box>
                      <AlertTitle>
                        {selectedBatch.status?.toLowerCase() === 'pending_hr_approval' 
                          ? 'HR Approval Required' 
                          : selectedBatch.status?.toLowerCase() === 'pending_accountant_approval'
                          ? 'Accountant Approval Required'
                          : 'No Action Required'
                        }
                      </AlertTitle>
                      <AlertDescription>
                        {selectedBatch.status?.toLowerCase() === 'pending_hr_approval' 
                          ? 'This batch is waiting for HR approval before it can proceed to the next stage.'
                          : selectedBatch.status?.toLowerCase() === 'pending_accountant_approval'
                          ? 'This batch has been approved by HR and is now waiting for accountant approval.'
                          : 'This batch has completed all approval stages.'
                        }
                      </AlertDescription>
                    </Box>
                  </Alert>
                </Box>

                <Divider />

                {/* Employee Details */}
                <Box>
                  <Heading size="sm" color="gray.800" mb={4}>
                    Employee Details ({selectedBatch.batch_items?.length || 0} employees)
                  </Heading>
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th>Employee</Th>
                          <Th>Basic Salary</Th>
                          <Th>Allowances</Th>
                          <Th>Bonus</Th>
                          <Th>Leave Allowance</Th>
                          <Th>Deductions</Th>
                          <Th>Tax Amount</Th>
                          <Th>Settlement Amount</Th>
                          <Th>Net Salary</Th>
                          <Th>Transfer Charge</Th>
                          <Th>Pay Type</Th>
                          <Th>Payment Status</Th>
                          <Th>Refund Amount</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {selectedBatch.batch_items?.length > 0 ? (
                          selectedBatch.batch_items.map((item, index) => (
                            <Tr key={index}>
                              <Td>
                                <VStack align="start" spacing={1}>
                                  <Text fontWeight="600" fontSize="sm">
                                    {item.employee_name || 'N/A'}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    ID: {item.employee_id || 'N/A'}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    {item.employee_email || 'N/A'}
                                  </Text>
                                </VStack>
                              </Td>
                              <Td>
                                <Text fontSize="sm" fontWeight="600">
                                  {formatCurrency(item.basic_salary || 0, planInfo?.country)}
                                </Text>
                              </Td>
                              <Td>
                                <Text fontSize="sm">
                                  {formatCurrency(item.allowances || 0, planInfo?.country)}
                                </Text>
                              </Td>
                              <Td>
                                <Text fontSize="sm" fontWeight="500" color="blue.600">
                                  {formatCurrency(item.bonus || 0, planInfo?.country)}
                                </Text>
                              </Td>
                              <Td>
                                <Text fontSize="sm" fontWeight="500" color="green.600">
                                  {formatCurrency(item.leave_allowance || 0, planInfo?.country)}
                                </Text>
                              </Td>
                              <Td>
                                <Text fontSize="sm">
                                  {formatCurrency(item.deductions || 0, planInfo?.country)}
                                </Text>
                              </Td>
                              <Td>
                                <Text fontSize="sm" fontWeight="500" color="red.600">
                                  {formatCurrency(item.tax_amount || 0, planInfo?.country)}
                                </Text>
                              </Td>
                              <Td>
                                <Text fontSize="sm" fontWeight="500" color="orange.600">
                                  {formatCurrency(item.settlement_amount || 0, planInfo?.country)}
                                </Text>
                              </Td>
                              <Td>
                                <Text fontSize="sm" fontWeight="600" color="green.600">
                                  {formatCurrency(item.net_salary || 0, planInfo?.country)}
                                </Text>
                              </Td>
                              <Td>
                                <Text fontSize="sm" fontWeight="500" color="purple.600">
                                  {formatCurrency(item.flutterwave_fee || 0, planInfo?.country)}
                                </Text>
                              </Td>
                              <Td>
                                <Badge colorScheme="blue" size="sm">
                                  {item.pay_type === 'regular_pay' ? 'Regular Pay' : 
                                   item.pay_type === 'total_pay' ? 'Total Pay' : 
                                   item.pay_type === 'net_salary' ? 'Net Salary' : 
                                   item.pay_type || 'N/A'}
                                </Badge>
                              </Td>
                              <Td>
                                <Badge 
                                  colorScheme={
                                    item.payment_status === 'PAID' || item.payment_status === 'SUCCESS' || item.payment_status === 'COMPLETED' ? 'green' :
                                    item.payment_status === 'FAILED' ? 'red' :
                                    item.payment_status === 'PROCESSING' ? 'yellow' :
                                    'gray'
                                  } 
                                  size="sm"
                                >
                                  {item.payment_status || 'PENDING'}
                                </Badge>
                                {item.failure_reason && (
                                  <Text fontSize="xs" color="red.600" mt={1}>
                                    {item.failure_reason}
                                  </Text>
                                )}
                              </Td>
                              <Td>
                                {item.payment_status === 'FAILED' && item.refund_amount ? (
                                  <VStack align="start" spacing={0}>
                                    <Text fontSize="sm" fontWeight="600" color="red.600">
                                      {formatCurrency(item.refund_amount, planInfo?.country)}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                      Will be refunded
                                    </Text>
                                  </VStack>
                                ) : (
                                  <Text fontSize="sm" color="gray.400">—</Text>
                                )}
                              </Td>
                            </Tr>
                          ))
                        ) : (
                          <Tr>
                            <Td colSpan={11}>
                              <VStack spacing={2} py={4}>
                                <FiFileText size={24} color="gray" />
                                <Text color="gray.500">No employee details available</Text>
                              </VStack>
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onViewClose}>
              Close
            </Button>
            {selectedBatch?.status?.toLowerCase() === 'pending_hr_approval' && (
              <Button colorScheme="orange" mr={3}>
                HR Approve
              </Button>
            )}
            {selectedBatch?.status?.toLowerCase() === 'pending_accountant_approval' && (
              <Button colorScheme="blue">
                Accountant Approve
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default BatchManagement;